/**
 * animations.js — wirtschaft. am markt
 * ---------------------------------------------------------------------------
 * GSAP orchestration layer (see docs/ANIMATIONS.md — the contract for every
 * future section). One declarative system: sections opt in via data-animate
 * attributes, this file turns them into ScrollTrigger reveals in house style.
 *
 *   data-animate            → rise: fade + 28px up
 *   data-animate="headline" → SplitText word rise from a mask; a trailing
 *                             "." pops in last (the brand's dot-tap motif,
 *                             same choreography as the menu overlay)
 *   data-animate="media"    → clip wipe upwards + subtle inner de-zoom
 *   data-animate-group      → staggers its direct children as one block
 *
 * Signature moment (Speisekarte): dotted dividers draw themselves from the
 * left while the riding illustrations (fish/cow) fade in on top.
 *
 * Boundaries with existing motion — do not double-animate:
 *   - hero.js owns the homepage hero (load sweep + mouse strip)
 *   - gallery.js owns the collage items (reveal + parallax)
 *   - header/mini-cart/take-away overlays are CSS choreography
 *   - hover/focus states stay CSS transitions
 *
 * Lenis (smooth-scroll.js) drives its own rAF loop → we only couple
 * ScrollTrigger.update to its scroll event, never start a second loop.
 * All motion lives inside a prefers-reduced-motion matchMedia context:
 * with reduced motion there simply are no animations and the page is
 * fully visible (initial states are set by JS only — no-JS visitors see
 * everything too).
 */

(() => {
  "use strict";

  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;

  gsap.registerPlugin(ScrollTrigger);
  var hasSplit = typeof SplitText !== "undefined";
  if (hasSplit) gsap.registerPlugin(SplitText);

  /* ---- House constants (docs/ANIMATIONS.md) — change values only here --- */
  var ANIM = {
    dur: { xs: 0.35, sm: 0.6, md: 0.9, lg: 1.3 },
    ease: "expo.out", // house ease — mirrors --ease-out-expo (fast in, soft landing)
    easeTap: "back.out(2.5)", // dot-tap only
    dist: 28, // reveal rise distance, matches the gallery reveal
    stagger: 0.09,
    start: "top 86%" // when a reveal fires relative to the viewport
  };

  /* Lenis sync: couple the update, never a second rAF loop. */
  if (window.lenis) window.lenis.on("scroll", ScrollTrigger.update);

  var cleanups = [];
  var motionActive = false;

  /* ---- Variants ---------------------------------------------------------- */

  /* CSS transitions on a property GSAP is tweening (e.g. the global
     `a { transition: opacity … }` hover dim) make the computed value chase
     the tween 150ms behind — the reveal degrades to a flat fade. Mute
     inline while the tween owns the element, restore afterwards. */
  function muteTransitions(targets) {
    gsap.set(targets, { transition: "none" });
  }
  function restoreTransitions(targets) {
    gsap.set(targets, { clearProps: "transition" });
  }

  function revealRise(el) {
    muteTransitions(el);
    var tween = gsap.from(el, {
      autoAlpha: 0,
      y: ANIM.dist,
      duration: ANIM.dur.md,
      ease: ANIM.ease,
      scrollTrigger: { trigger: el, start: ANIM.start, once: true },
      onComplete: function () {
        restoreTransitions(el);
      }
    });
    cleanups.push(function () {
      tween.kill();
      gsap.set(el, { clearProps: "opacity,visibility,transform,transition" });
    });
  }

  /* Headline: words rise out of a mask, the trailing dot taps in last —
     the sentence finishes itself, like the menu overlay links. */
  function revealHeadline(el) {
    if (!hasSplit) return revealRise(el);

    var split = new SplitText(el, { type: "words", mask: "words" });
    var words = split.words;
    if (!words.length) {
      split.revert();
      return revealRise(el);
    }

    /* Detach a trailing "." into its own span so it can land after. */
    var dot = null;
    var last = words[words.length - 1];
    var text = last.textContent;
    if (text.length > 1 && text.charAt(text.length - 1) === ".") {
      last.textContent = text.slice(0, -1);
      dot = document.createElement("span");
      dot.textContent = ".";
      dot.style.display = "inline-block";
      last.appendChild(dot);
    }

    var tl = gsap.timeline({
      scrollTrigger: { trigger: el, start: ANIM.start, once: true }
    });
    tl.from(words, {
      yPercent: 115,
      duration: ANIM.dur.md,
      ease: ANIM.ease,
      stagger: ANIM.stagger
    });
    if (dot) {
      tl.from(
        dot,
        {
          autoAlpha: 0,
          y: "-0.35em",
          scale: 0.4,
          transformOrigin: "bottom center",
          duration: ANIM.dur.xs,
          ease: ANIM.easeTap
        },
        ">-0.1"
      );
    }

    cleanups.push(function () {
      tl.kill();
      split.revert(); /* also removes the injected dot span */
    });
  }

  /* Media: block wipes upwards (clip), the image inside settles from a
     subtle zoom — same rising direction as everything else. */
  function revealMedia(el) {
    var media = Array.prototype.slice.call(el.querySelectorAll("img, video"));
    muteTransitions([el].concat(media));
    var clear = function () {
      /* An inline inset(0) would keep clipping children that intentionally
         overflow (e.g. teaser accents) — drop it once the wipe is done. */
      gsap.set(el, { clearProps: "clipPath,transition" });
      if (media.length) gsap.set(media, { clearProps: "transform,transition" });
    };
    var tl = gsap.timeline({
      scrollTrigger: { trigger: el, start: ANIM.start, once: true },
      onComplete: clear
    });
    tl.fromTo(
      el,
      { clipPath: "inset(100% 0% 0% 0%)" },
      { clipPath: "inset(0% 0% 0% 0%)", duration: ANIM.dur.lg, ease: ANIM.ease }
    );
    if (media.length) {
      tl.from(media, { scale: 1.1, duration: ANIM.dur.lg, ease: ANIM.ease }, 0);
    }
    cleanups.push(function () {
      tl.kill();
      clear();
    });
  }

  /* Group: direct children rise as one staggered block. */
  function revealGroup(el) {
    var children = Array.prototype.slice.call(el.children);
    if (!children.length) return;
    muteTransitions(children);
    var tween = gsap.from(children, {
      autoAlpha: 0,
      y: ANIM.dist,
      duration: ANIM.dur.md,
      ease: ANIM.ease,
      stagger: ANIM.stagger,
      scrollTrigger: { trigger: el, start: ANIM.start, once: true },
      onComplete: function () {
        restoreTransitions(children);
      }
    });
    cleanups.push(function () {
      tween.kill();
      gsap.set(children, { clearProps: "opacity,visibility,transform,transition" });
    });
  }

  /* ---- Signature: Speisekarte dividers ----------------------------------
     The dotted line draws itself from the left; the riding illustration
     (fish/cow) fades in on top of it. Scoped custom timeline — the one
     place beyond the declarative system. */
  function initSpeisekarte(scope) {
    scope.querySelectorAll(".speisekarte__break").forEach(function (brk) {
      if (brk.dataset.animReady) return;
      brk.dataset.animReady = "true";

      var line = brk.querySelector(".speisekarte__line");
      var illu = brk.querySelector(".speisekarte__break-illu");
      if (!line) return;

      var tl = gsap.timeline({
        scrollTrigger: { trigger: brk, start: ANIM.start, once: true }
      });
      tl.from(line, {
        scaleX: 0,
        transformOrigin: "left center",
        duration: ANIM.dur.lg,
        ease: ANIM.ease
      });
      if (illu) {
        tl.from(
          illu,
          {
            autoAlpha: 0,
            x: -32,
            duration: ANIM.dur.md,
            ease: ANIM.ease
          },
          0.15
        );
      }
      cleanups.push(function () {
        tl.kill();
        delete brk.dataset.animReady;
        gsap.set(line, { clearProps: "transform" });
        if (illu) gsap.set(illu, { clearProps: "opacity,visibility,transform" });
      });
    });
  }

  /* ---- Declarative collector --------------------------------------------- */

  function initScope(scope) {
    scope.querySelectorAll("[data-animate]").forEach(function (el) {
      if (el.dataset.animReady) return;
      el.dataset.animReady = "true";
      var variant = el.getAttribute("data-animate");
      if (variant === "headline") revealHeadline(el);
      else if (variant === "media") revealMedia(el);
      else revealRise(el);
    });

    scope.querySelectorAll("[data-animate-group]").forEach(function (el) {
      if (el.dataset.animReady) return;
      el.dataset.animReady = "true";
      revealGroup(el);
    });

    initSpeisekarte(scope);
  }

  function teardown() {
    cleanups.forEach(function (fn) {
      fn();
    });
    cleanups = [];
    document.querySelectorAll("[data-anim-ready]").forEach(function (el) {
      delete el.dataset.animReady;
    });
    ScrollTrigger.getAll().forEach(function (st) {
      st.kill();
    });
  }

  /* ---- Boot --------------------------------------------------------------
     Split/measure only after the web fonts are in — otherwise SplitText
     wraps words on fallback metrics and ScrollTrigger measures a page that
     is about to reflow. */
  var fontsReady =
    document.fonts && document.fonts.ready
      ? document.fonts.ready
      : Promise.resolve();

  var mm = gsap.matchMedia();
  mm.add("(prefers-reduced-motion: no-preference)", function () {
    motionActive = true;
    fontsReady.then(function () {
      if (!motionActive) return;
      initScope(document);
      ScrollTrigger.refresh();
    });
    return function () {
      motionActive = false;
      teardown();
    };
  });

  /* Theme editor: re-init freshly injected section markup. */
  document.addEventListener("shopify:section:load", function (event) {
    if (!motionActive) return;
    initScope(event.target);
    ScrollTrigger.refresh();
  });
})();
